import { Box, Button, Center, Container, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import type { OrganisationConnection, UserResponse } from '@/api/generated/models';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import {
  getGetOrganisationQueryKey,
  useDeactivateOrganisation,
  useGetOrganisation,
  useReactivate,
  useRevokeOrganisationTokens
} from '@/api/generated/organisations/organisations';
import { useListUsers } from '@/api/generated/users/users';
import {
  BackButton,
  CardSection,
  ConfirmActionModal,
  ListPagination,
  MobileActionBar,
  StatusPill,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle,
  Toast
} from '@/components/common';
import { formatDate, formatDateTime, formatRole } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

interface OrganisationDetail {
  id: string;
  name: string;
  address: string | null;
  country: string;
  currency: string;
  isActive: boolean;
  createdAt?: string;
}

const userColumns: [string, number, ('center' | undefined)?][] = [
  ['adminOrgDetail.colName', 180],
  ['adminOrgDetail.colEmail', 220],
  ['adminOrgDetail.colRole', 120],
  ['platformAdmin.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

const connectionColumns: [string, number, ('center' | undefined)?][] = [
  ['platformAdmin.colOrganization', 300],
  ['platformAdmin.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

export const AdminOrganizationDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [revokeSessionsModalOpen, setRevokeSessionsModalOpen] = useState(false);

  const {
    data: orgData,
    isLoading: orgLoading,
    isError: orgError
  } = useGetOrganisation(id ?? '', {
    query: { enabled: !!id }
  });
  const org = orgData as unknown as OrganisationDetail | undefined;

  const { data: usersData, isLoading: usersLoading } = useListUsers(
    { organisationId: id },
    { query: { enabled: !!id } }
  );
  const orgUsers: UserResponse[] = usersData ?? [];
  const { page, setPage, pageSize, setPageSize, totalPages, pageItems, offset } =
    useLocalPagination(orgUsers);

  const { data: connectionsData, isLoading: connectionsLoading } = useListOrganisationConnections(
    { organisationId: id },
    { query: { enabled: !!id } }
  );
  const connections: OrganisationConnection[] = connectionsData ?? [];
  const connectionRows = connections.map((connection) => ({
    id: connection.id,
    orgId: connection.orgAId === id ? connection.orgBId : connection.orgAId,
    orgName: connection.orgAId === id ? connection.orgBName : connection.orgAName,
    isActive: connection.isActive,
    connectedSince: connection.acceptedAt ?? connection.createdAt
  }));

  const [actionFeedback, setActionFeedback] = useState<{
    tone: 'success' | 'danger';
    messageKey: string;
  } | null>(null);

  const invalidateOrg = () =>
    queryClient.invalidateQueries({ queryKey: getGetOrganisationQueryKey(id ?? '') });

  const deactivateMutation = useDeactivateOrganisation({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      // Nema success poruke — perzistentni narančasti "obustavljena" banner
      // ispod već to javlja, dvije poruke odjednom bile bi duplicirane.
      onSuccess: () => {
        invalidateOrg();
        setSuspendModalOpen(false);
      },
      onError: () => {
        setSuspendModalOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminOrgDetail.suspendError' });
      }
    }
  });
  const reactivateMutation = useReactivate({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateOrg();
        setRestoreModalOpen(false);
        setActionFeedback({ tone: 'success', messageKey: 'adminOrgDetail.restoreSuccess' });
      },
      onError: () => {
        setRestoreModalOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminOrgDetail.restoreError' });
      }
    }
  });
  const revokeTokensMutation = useRevokeOrganisationTokens({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        setRevokeSessionsModalOpen(false);
        setActionFeedback({
          tone: 'success',
          messageKey: 'adminOrgDetail.revokeSessionsSuccess'
        });
      },
      onError: () => {
        setRevokeSessionsModalOpen(false);
        setActionFeedback({ tone: 'danger', messageKey: 'adminOrgDetail.revokeSessionsError' });
      }
    }
  });

  if (orgLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!org) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {orgError ? t('adminOrgDetail.notFound') : t('users.roles.emptyState')}
        </Text>
      </Container>
    );
  }

  const suspended = !org.isActive;

  const orgContextLine = (
    <Text fz={13.5} fw={600} pt={4} style={{ color: 'var(--color-text-primary)' }}>
      {org.name}
    </Text>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/admin/organizations" />

        <Group justify="space-between" wrap="wrap" gap={12}>
          <Stack gap={8}>
            <Group gap={12} wrap="wrap">
              <Title
                order={2}
                fz={{ base: 22, sm: 24 }}
                style={{ color: 'var(--color-text-primary)' }}
              >
                {org.name}
              </Title>
              <StatusPill
                header
                label={t(
                  suspended ? 'platformAdmin.statusSuspended' : 'platformAdmin.statusActive'
                )}
                bg={
                  suspended ? 'var(--color-status-danger-soft)' : 'var(--color-status-success-soft)'
                }
                fg={suspended ? 'var(--color-status-danger)' : 'var(--color-status-success)'}
              />
            </Group>
          </Stack>
          <Group gap={12} visibleFrom="sm">
            <Button component={Link} to={`/admin/organizations/${id}/edit`} variant="outline">
              {t('adminOrgDetail.editButton')}
            </Button>
          </Group>
          <MobileActionBar>
            <Button component={Link} to={`/admin/organizations/${id}/edit`} variant="outline">
              {t('adminOrgDetail.editButton')}
            </Button>
          </MobileActionBar>
        </Group>

        {actionFeedback && (
          <Toast tone={actionFeedback.tone}>
            {t(actionFeedback.messageKey, { name: org.name })}
          </Toast>
        )}

        {suspended && <Toast tone="warning">{t('adminOrgDetail.suspendedWarningMessage')}</Toast>}

        <CardSection title={t('adminOrgDetail.overviewTitle')}>
          <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
            <Group gap={40} p={20} wrap="wrap" style={{ minWidth: 700 }}>
              <Stack gap={4} style={{ flex: '1 1 160px' }}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('adminOrgDetail.fieldName')}
                </Text>
                <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                  {org.name}
                </Text>
              </Stack>
              <Stack gap={4} style={{ flex: '1 1 160px' }}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('adminOrgDetail.fieldStatus')}
                </Text>
                <Text
                  fz={14}
                  fw={500}
                  style={{
                    color: suspended ? 'var(--color-status-danger)' : 'var(--color-text-primary)'
                  }}
                >
                  {suspended ? t('platformAdmin.statusSuspended') : t('platformAdmin.statusActive')}
                </Text>
              </Stack>
              <Stack gap={4} style={{ flex: '1 1 160px' }}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('adminOrgDetail.fieldUserCount')}
                </Text>
                <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                  {orgUsers.length}
                </Text>
              </Stack>
              <Stack gap={4} style={{ flex: '1 1 160px' }}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('adminOrgDetail.fieldRegistered')}
                </Text>
                <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
                  {formatDate(org.createdAt)}
                </Text>
              </Stack>
            </Group>
          </Box>

          <Stack gap={12} p={14} hiddenFrom="sm">
            {(
              [
                ['adminOrgDetail.fieldName', org.name],
                [
                  'adminOrgDetail.fieldStatus',
                  suspended ? t('platformAdmin.statusSuspended') : t('platformAdmin.statusActive')
                ],
                ['adminOrgDetail.fieldUserCount', String(orgUsers.length)],
                ['adminOrgDetail.fieldRegistered', formatDate(org.createdAt)]
              ] as [string, string][]
            ).map(([labelKey, value], index, arr) => (
              <Group
                key={labelKey}
                justify="space-between"
                wrap="nowrap"
                pb={8}
                style={{
                  borderBottom:
                    index === arr.length - 1 ? undefined : '1px solid var(--color-border-soft)'
                }}
              >
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t(labelKey)}
                </Text>
                <Text
                  fz={12}
                  fw={500}
                  style={{
                    color:
                      suspended && labelKey === 'adminOrgDetail.fieldStatus'
                        ? 'var(--color-status-danger)'
                        : 'var(--color-text-primary)'
                  }}
                >
                  {value}
                </Text>
              </Group>
            ))}
          </Stack>
        </CardSection>

        <CardSection title={t('adminOrgDetail.connectionsTitle', { count: connectionRows.length })}>
          {!connectionsLoading && connectionRows.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('adminOrgDetail.connectionsEmptyState')}
              </Text>
            </Center>
          )}

          {connectionRows.length > 0 && (
            <>
              <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
                <div style={{ minWidth: 700 }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {connectionColumns.map(([key, width, align]) => (
                      <Text
                        key={key}
                        fz={10}
                        fw={600}
                        w={width}
                        ta={align}
                        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                      >
                        {t(key)}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
                    >
                      {t('adminOrgDetail.colConnectedSince')}
                    </Text>
                    <Text
                      fz={10}
                      fw={600}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('platformAdmin.colAction')}
                    </Text>
                  </div>

                  {connectionRows.map((row, index) => (
                    <div
                      key={row.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        w={300}
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {row.orgName}
                      </Text>
                      <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                        <StatusPill
                          label={t(
                            row.isActive
                              ? 'platformAdmin.statusActive'
                              : 'platformAdmin.statusSuspended'
                          )}
                          bg={
                            row.isActive
                              ? 'var(--color-status-success-soft)'
                              : 'var(--color-status-danger-soft)'
                          }
                          fg={
                            row.isActive
                              ? 'var(--color-status-success)'
                              : 'var(--color-status-danger)'
                          }
                        />
                      </div>
                      <Text fz={13} style={{ color: 'var(--color-text-muted)', flex: 1 }}>
                        {formatDate(row.connectedSince)}
                      </Text>
                      <Text
                        component={Link}
                        to={`/admin/organizations/${row.orgId}`}
                        fz={12}
                        fw={600}
                        w={100}
                        ta="right"
                        style={{
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('platformAdmin.detailsLink')}
                      </Text>
                    </div>
                  ))}
                </div>
              </Box>

              <Stack gap={0} hiddenFrom="sm">
                {connectionRows.map((row, index) => (
                  <Stack
                    key={row.id}
                    gap={8}
                    p={14}
                    style={{
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                        {row.orgName}
                      </Text>
                      <StatusPill
                        label={t(
                          row.isActive
                            ? 'platformAdmin.statusActive'
                            : 'platformAdmin.statusSuspended'
                        )}
                        bg={
                          row.isActive
                            ? 'var(--color-status-success-soft)'
                            : 'var(--color-status-danger-soft)'
                        }
                        fg={
                          row.isActive
                            ? 'var(--color-status-success)'
                            : 'var(--color-status-danger)'
                        }
                      />
                    </Group>
                    <Group justify="space-between" align="center" wrap="nowrap" pt={4}>
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('adminOrgDetail.colConnectedSince')}: {formatDate(row.connectedSince)}
                      </Text>
                      <Text
                        component={Link}
                        to={`/admin/organizations/${row.orgId}`}
                        fz={12}
                        fw={600}
                        style={{
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('platformAdmin.detailsLink')}
                      </Text>
                    </Group>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </CardSection>

        <CardSection title={t('adminOrgDetail.usersTitle', { count: orgUsers.length })}>
          {!usersLoading && orgUsers.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.roles.emptyState')}
              </Text>
            </Center>
          )}

          {orgUsers.length > 0 && (
            <>
              <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
                <div style={{ minWidth: 900 }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {userColumns.map(([key, width, align]) => (
                      <Text
                        key={key}
                        fz={10}
                        fw={600}
                        w={width}
                        ta={align}
                        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                      >
                        {t(key)}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
                    >
                      {t('adminOrgDetail.colLastLogin')}
                    </Text>
                    <Text
                      fz={10}
                      fw={600}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('platformAdmin.colAction')}
                    </Text>
                  </div>

                  {pageItems.map((row, index) => (
                    <div
                      key={row.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        w={180}
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {row.firstName} {row.lastName}
                      </Text>
                      <Text fz={13} w={220} style={{ color: 'var(--color-text-secondary)' }}>
                        {row.email}
                      </Text>
                      <Text fz={13} w={120} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatRole(row.role)}
                      </Text>
                      <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                        <StatusPill
                          label={t(
                            row.isActive
                              ? 'platformAdmin.statusActive'
                              : 'adminOrgDetail.statusInactive'
                          )}
                          bg={
                            row.isActive
                              ? 'var(--color-status-success-soft)'
                              : 'var(--color-status-danger-soft)'
                          }
                          fg={
                            row.isActive
                              ? 'var(--color-status-success)'
                              : 'var(--color-status-danger)'
                          }
                        />
                      </div>
                      <Text
                        fz={13}
                        style={{
                          color: 'var(--color-text-muted)',
                          flex: 1,
                          fontFamily: 'var(--mantine-font-family-monospace)'
                        }}
                      >
                        {formatDateTime(row.lastLoginAt) ?? t('users.roles.neverLoggedIn')}
                      </Text>
                      <Text
                        component={Link}
                        to={`/admin/users/${row.id}`}
                        fz={12}
                        fw={600}
                        w={100}
                        ta="right"
                        style={{
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('platformAdmin.detailsLink')}
                      </Text>
                    </div>
                  ))}
                </div>
              </Box>

              <Stack gap={0} hiddenFrom="sm">
                {pageItems.map((row, index) => (
                  <Stack
                    key={row.id}
                    gap={8}
                    p={14}
                    style={{
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                        {row.firstName} {row.lastName}
                      </Text>
                      <StatusPill
                        label={t(
                          row.isActive
                            ? 'platformAdmin.statusActive'
                            : 'adminOrgDetail.statusInactive'
                        )}
                        bg={
                          row.isActive
                            ? 'var(--color-status-success-soft)'
                            : 'var(--color-status-danger-soft)'
                        }
                        fg={
                          row.isActive
                            ? 'var(--color-status-success)'
                            : 'var(--color-status-danger)'
                        }
                      />
                    </Group>
                    <Text fz={11.5} style={{ color: 'var(--color-text-secondary)' }}>
                      {row.email}
                    </Text>
                    <Group justify="space-between" align="center" wrap="nowrap" pt={4}>
                      <Group gap={6} wrap="nowrap">
                        <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                          {t('adminOrgDetail.colRole')}:
                        </Text>
                        <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                          {formatRole(row.role)}
                        </Text>
                      </Group>
                      <Text
                        fz={10}
                        style={{
                          color: 'var(--color-text-muted)',
                          fontFamily: 'var(--mantine-font-family-monospace)'
                        }}
                      >
                        {formatDateTime(row.lastLoginAt) ?? t('users.roles.neverLoggedIn')}
                      </Text>
                    </Group>
                    <Group justify="flex-end" pt={6}>
                      <Text
                        component={Link}
                        to={`/admin/users/${row.id}`}
                        fz={12}
                        fw={600}
                        style={{
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('platformAdmin.detailsLink')}
                      </Text>
                    </Group>
                  </Stack>
                ))}
              </Stack>

              <ListPagination
                label={t('users.roles.showing', {
                  from: orgUsers.length === 0 ? 0 : offset + 1,
                  to: offset + pageItems.length,
                  total: orgUsers.length
                })}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </CardSection>

        <Group gap={16} align="stretch" wrap="wrap">
          <Stack
            gap={12}
            p={{ base: 16, sm: 20 }}
            style={{
              flex: '1 1 400px',
              minWidth: 320,
              borderRadius: 12,
              border: '1px solid var(--color-border-soft)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            <Group justify="space-between" wrap="wrap" visibleFrom="sm">
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {t('adminOrgDetail.suspendTitle')}
              </Text>
              <Button
                size="xs"
                onClick={() => (suspended ? setRestoreModalOpen(true) : setSuspendModalOpen(true))}
                styles={{
                  root: suspended
                    ? {}
                    : {
                        backgroundColor: 'var(--color-status-danger-solid)',
                        color: 'var(--color-text-on-secondary)'
                      }
                }}
              >
                {suspended ? t('adminOrgDetail.restoreButton') : t('adminOrgDetail.suspendButton')}
              </Button>
            </Group>
            <Text
              fz={{ base: 13.5, sm: 14 }}
              fw={600}
              style={{ color: 'var(--color-text-primary)' }}
              hiddenFrom="sm"
            >
              {t('adminOrgDetail.suspendTitle')}
            </Text>
            <Text
              fz={{ base: 11.5, sm: 12.5 }}
              lh={{ base: '16px', sm: '18px' }}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t('adminOrgDetail.suspendDescription')}
            </Text>
            <Box hiddenFrom="sm">
              <Button
                size="xs"
                onClick={() => (suspended ? setRestoreModalOpen(true) : setSuspendModalOpen(true))}
                styles={{
                  root: suspended
                    ? {}
                    : {
                        backgroundColor: 'var(--color-status-danger-solid)',
                        color: 'var(--color-text-on-secondary)'
                      }
                }}
              >
                {suspended ? t('adminOrgDetail.restoreButton') : t('adminOrgDetail.suspendButton')}
              </Button>
            </Box>
          </Stack>

          <Stack
            gap={12}
            p={{ base: 16, sm: 20 }}
            style={{
              flex: '1 1 400px',
              minWidth: 320,
              borderRadius: 12,
              border: '1px solid var(--color-border-soft)',
              backgroundColor: 'var(--color-bg-surface)'
            }}
          >
            <Group justify="space-between" wrap="wrap" visibleFrom="sm">
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {t('adminOrgDetail.securityTitle')}
              </Text>
              <Button
                color="red"
                size="xs"
                variant="outline"
                onClick={() => setRevokeSessionsModalOpen(true)}
              >
                {t('adminOrgDetail.securityButton')}
              </Button>
            </Group>
            <Text
              fz={{ base: 13.5, sm: 14 }}
              fw={600}
              style={{ color: 'var(--color-text-primary)' }}
              hiddenFrom="sm"
            >
              {t('adminOrgDetail.securityTitle')}
            </Text>
            <Text
              fz={{ base: 11.5, sm: 12.5 }}
              lh={{ base: '16px', sm: '18px' }}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t('adminOrgDetail.securityDescription')}
            </Text>
            <Box hiddenFrom="sm">
              <Button
                color="red"
                size="xs"
                variant="outline"
                onClick={() => setRevokeSessionsModalOpen(true)}
              >
                {t('adminOrgDetail.securityButton')}
              </Button>
            </Box>
          </Stack>
        </Group>
      </Stack>

      <ConfirmActionModal
        opened={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        onConfirm={() => {
          setActionFeedback(null);
          deactivateMutation.mutate({ id: id ?? '' });
        }}
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
        title={t('adminOrgDetail.suspendModalTitle')}
        contextLine={orgContextLine}
        message={t('adminOrgDetail.suspendModalMessage')}
        confirmLabel={t('adminOrgDetail.suspendButton')}
        danger
      />

      <ConfirmActionModal
        opened={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        onConfirm={() => {
          setActionFeedback(null);
          reactivateMutation.mutate({ id: id ?? '' });
        }}
        loading={reactivateMutation.isPending}
        isPaused={reactivateMutation.isPaused}
        title={t('adminOrgDetail.restoreModalTitle')}
        contextLine={orgContextLine}
        message={t('adminOrgDetail.restoreModalMessage')}
        confirmLabel={t('adminOrgDetail.restoreButton')}
      />

      <ConfirmActionModal
        opened={revokeSessionsModalOpen}
        onClose={() => setRevokeSessionsModalOpen(false)}
        onConfirm={() => {
          setActionFeedback(null);
          revokeTokensMutation.mutate({ id: id ?? '' });
        }}
        loading={revokeTokensMutation.isPending}
        isPaused={revokeTokensMutation.isPaused}
        title={t('adminOrgDetail.revokeSessionsModalTitle')}
        contextLine={orgContextLine}
        message={t('adminOrgDetail.revokeSessionsModalMessage')}
        confirmLabel={t('adminOrgDetail.revokeSessionsConfirm')}
        danger
      />
    </Container>
  );
};
